import React from "react";
import { motion } from "framer-motion";
import {
  Headphones,
  UserPlus,
  MessageSquare,
  ShoppingCart,
  Calendar,
  HelpCircle,
  TrendingUp,
  Building2,
  type LucideIcon,
} from "lucide-react";
import AnimatedSection from "./AnimatedSection";
import SectionHeader from "./SectionHeader";

type Service = {
  title: string;
  description: string;
  icon: LucideIcon;
  gradient: string;
};

const services: Service[] = [
  {
    title: "Customer Support Chatbots",
    description: "Resolve queries instantly with AI-powered 24/7 support automation.",
    icon: Headphones,
    gradient: "cbot-service-card__icon--blue",
  },
  {
    title: "Lead Generation Bots",
    description: "Capture, qualify, and nurture leads through intelligent conversations.",
    icon: UserPlus,
    gradient: "cbot-service-card__icon--purple",
  },
  {
    title: "WhatsApp Chatbots",
    description: "Engage customers on WhatsApp with automated, personalized messaging.",
    icon: MessageSquare,
    gradient: "cbot-service-card__icon--green",
  },
  {
    title: "Ecommerce Chatbots",
    description: "Boost sales with product recommendations and order assistance.",
    icon: ShoppingCart,
    gradient: "cbot-service-card__icon--orange",
  },
  {
    title: "Appointment Booking Bots",
    description: "Automate scheduling, reminders, and calendar sync effortlessly.",
    icon: Calendar,
    gradient: "cbot-service-card__icon--cyan",
  },
  {
    title: "FAQ Chatbots",
    description: "Answer common questions instantly and reduce support workload.",
    icon: HelpCircle,
    gradient: "cbot-service-card__icon--pink",
  },
  {
    title: "AI Sales Assistants",
    description: "Guide prospects through the funnel with smart sales conversations.",
    icon: TrendingUp,
    gradient: "cbot-service-card__icon--violet",
  },
  {
    title: "Internal Business Bots",
    description: "Streamline HR, IT, and internal workflows with custom AI bots.",
    icon: Building2,
    gradient: "cbot-service-card__icon--indigo",
  },
];

const ChatBotServices: React.FC = () => (
  <AnimatedSection as="section" className="cbot-section cbot-section--light" aria-labelledby="cbot-services-title">
    <div className="cbot-container">
      <SectionHeader
        eyebrow="Our Chatbot Services"
        title="AI Chatbot Solutions Built for Every Business Need"
        text="From customer support to sales automation — we build intelligent chatbots tailored to your goals."
        light
      />

      <div className="cbot-service-grid">
        {services.map((service, index) => {
          const Icon = service.icon;
          return (
            <motion.article
              key={service.title}
              className="cbot-service-card"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.06 }}
              whileHover={{ y: -8, transition: { duration: 0.25 } }}
            >
              <div className={`cbot-service-card__icon ${service.gradient}`}>
                <Icon size={24} aria-hidden="true" />
              </div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
            </motion.article>
          );
        })}
      </div>
    </div>
  </AnimatedSection>
);

export default ChatBotServices;
